import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { scrollToId } from "@/lib/api";
import { IMG, VIDEO } from "@/lib/assets";

const TITLE_LINES = ["VOTRE AUTOMOBILE.", "NOTRE EXPERTISE."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <video
          data-testid="hero-video"
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={IMG.hero}
        >
          <source src={VIDEO.hero} type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/65 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 container-x flex-1 flex flex-col justify-center pt-32 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-flex items-center gap-2 self-start border border-gold/30 bg-gold/5 rounded-full px-4 py-1.5 mb-8"
          data-testid="hero-badge"
        >
          <ShieldCheck size={14} className="text-gold" />
          <span className="text-[11px] uppercase tracking-[0.25em] text-gold-light">
            Firme privée de gestion automobile — Québec
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-display font-medium text-[13vw] sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight text-white max-w-5xl"
        >
          {TITLE_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "gold-text" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-7 text-base sm:text-lg text-slate-300 font-light max-w-2xl leading-relaxed"
        >
          Un service privé de gestion automobile clé en main, de l'acquisition à la revente.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-4 text-sm sm:text-base text-slate-400 font-light max-w-2xl leading-relaxed"
        >
          Gestion Automobile Exécutive accompagne ses clients dans toutes les étapes de leur parcours
          automobile. Achat, vente, remplacement, financement, recherche personnalisée, gestion de flotte
          et accompagnement automobile : nous simplifions chaque étape afin que vous puissiez profiter de
          votre véhicule sans les tracas qui l'accompagnent.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button data-testid="hero-cta-search-car" onClick={() => scrollToId("#recherche")} className="btn-gold">
            Trouver mon véhicule <ArrowRight size={16} />
          </button>
          <button data-testid="hero-cta-advisor" onClick={() => scrollToId("#contact")} className="btn-outline">
            Parler à un conseiller
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="relative z-10 border-t border-white/10 bg-ink/40 backdrop-blur-md"
        data-testid="hero-inventory-strip"
      >
        <div className="container-x py-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-8">
          <span className="font-mono text-2xl sm:text-3xl text-gold-light" data-testid="hero-inventory-count">600+</span>
          <p className="text-xs sm:text-sm text-slate-300 font-light leading-snug max-w-xl">
            véhicules d'occasion accessibles à travers notre réseau de garages partenaires — nous ne
            gardons aucun inventaire sur place, nous dénichons le vôtre.
          </p>
          <button
            data-testid="hero-cta-inventory"
            onClick={() => scrollToId("#inventaire")}
            className="sm:ml-auto text-[11px] uppercase tracking-[0.2em] text-gold-light hover:text-white transition-colors flex items-center gap-2"
          >
            Explorer <ArrowRight size={13} />
          </button>
        </div>
      </motion.div>
    </section>
  );
}
